import {
  isResidenceType,
  presenceMonthsWarning,
  RESIDENCE_TYPE_LABELS,
  RESIDENCE_TYPES,
  residenceTypeLabel,
  type SurveyV2,
} from '@ghp/domain'
import { Card, CardBody, CardHeader, CountInput, Field, Select } from '@ghp/ui'
import { Home } from 'lucide-react'

import { NumberField, TextField } from './SurveyForm'
import s from './SurveyForm.module.css'

/** Principale, secondaire, locative (Sentry TERRAIN-9) : les libellés du domaine, ceux du rapport. */
const RESIDENCE_TYPE_OPTIONS = RESIDENCE_TYPES.map((value) => ({
  value,
  label: RESIDENCE_TYPE_LABELS[value],
}))

/**
 * Le retour en arrière, que tous les autres contrôles de la fiche portent déjà
 * (`PRESENCE_OPTIONS`, `PRESENT_OPTIONS`, `KIND_OPTIONS`) et que celui-ci n'avait pas : un agent
 * qui tape « Commerce » par erreur sur un appartement doit pouvoir revenir à « rien » (M21).
 *
 * ⚠️ Une sentinelle d'écran, jamais écrite dans le relevé — la fonction de rappel la retraduit en
 * `null`. `Select` n'expose pas d'autre moyen d'effacer une sélection.
 */
const UNSET_RESIDENCE_TYPE = 'unset'

const UNSET_OPTION = { value: UNSET_RESIDENCE_TYPE, label: 'Non renseigné' }

/**
 * Les décomptes du logement. Libellés en français, clés du schéma en anglais — la frontière
 * du dépôt passe entre l'identifiant et la phrase, pas entre le technique et le métier.
 */
const DWELLING_FIELDS = [
  { key: 'constructionYear', label: 'Année de construction' },
  { key: 'connectedDwellingsCount', label: 'Logements raccordés' },
  { key: 'mainRoomsCount', label: 'Pièces principales' },
  { key: 'occupantsCount', label: 'Occupants' },
  { key: 'presenceMonthsPerYear', label: 'Présence à l’année (mois)' },
] as const

/**
 * Les clés que cet écran écrit, **énumérées**.
 *
 * ⚠️ `Partial<NonNullable<SurveyV2['dwelling']>>` ne referme rien : le schéma est un
 * `looseObject`, donc son type inféré porte un catchall `{ [k: string]: unknown }` et
 * `setDwelling({ residencetype: 'floor' })` — minuscule — compilait, écrivait une clé parasite
 * dans le brouillon, passait le schéma de dépôt et atterrissait dans le JSONB, le vrai champ
 * restant vide. `Pick` rétablit la fermeture que l'ancienne signature par clé donnait.
 */
type DwellingPatch = Partial<
  Pick<
    NonNullable<SurveyV2['dwelling']>,
    (typeof DWELLING_FIELDS)[number]['key'] | 'residenceType' | 'referenceComplement'
  >
>

export interface DwellingCardProps {
  survey: SurveyV2
  /** Fiche transmise : tout se lit, rien ne se saisit (D4). */
  disabled?: boolean
  onChange?: (survey: SurveyV2) => void
}

/**
 * « Caractéristiques de l'habitation contrôlée » — **dans l'onglet Infos depuis #350**, dans l'ordre
 * du rapport papier (propriétaire, occupant, habitation). Elle vivait dans le relevé : elle s'y est
 * **déplacée**, elle n'y est pas recopiée — deux saisies d'un même décompte se contrediraient.
 *
 * ⚠️ **Un bloc à part, et c'est voulu** : le réglage par client (#355) la masque pour Nantes
 * Métropole, qui ne la demande pas (fiche d'ajustement 03). Les décomptes vont toujours dans
 * `survey.dwelling` : rien ne change pour le relevé transmis.
 */
export function DwellingCard({ survey, disabled, onChange }: DwellingCardProps) {
  const dwelling = survey.dwelling ?? {}
  const residenceType = dwelling.residenceType ?? null

  function setDwelling(patch: DwellingPatch): void {
    onChange?.({ ...survey, dwelling: { ...survey.dwelling, ...patch } })
  }

  /**
   * Un décompte, écrit sous une clé venue de {@link DWELLING_FIELDS}. La clé est **déjà** contrainte
   * à l'union fermée par le type du paramètre : la clé calculée est sûre, et c'est tout ce que
   * l'assertion dit — une faute de frappe, elle, ne compile plus au point d'appel.
   */
  function setCount(key: (typeof DWELLING_FIELDS)[number]['key'], value: number | null): void {
    setDwelling({ [key]: value } as DwellingPatch)
  }

  return (
    <Card>
      <CardHeader icon={<Home size={19} />} title="Caractéristiques de l’habitation contrôlée" />
      <CardBody>
        <div className={s.fields}>
          {DWELLING_FIELDS.map((field) =>
            field.key === 'constructionYear' ? (
              <NumberField
                key={field.key}
                label={field.label}
                // Non contrôlé : la valeur vit dans le DOM le temps de la frappe, ce qui laisse
                // l'agent taper « 197 » sans qu'un relevé partiellement écrit lui soit réécrit
                // sous les doigts. Le brouillon, lui, est mis à jour à chaque touche.
                defaultValue={dwelling[field.key] ?? null}
                disabled={disabled}
                onValueChange={(value) => setCount(field.key, value)}
              />
            ) : (
              // #258 — un décompte, donc le compteur des matrices : un seul composant de décompte.
              <Field
                key={field.key}
                label={field.label}
                warning={
                  field.key === 'presenceMonthsPerYear'
                    ? (presenceMonthsWarning(dwelling.presenceMonthsPerYear) ?? undefined)
                    : undefined
                }
              >
                {({ id }) => (
                  <CountInput
                    id={id}
                    label={field.label}
                    value={dwelling[field.key] ?? null}
                    disabled={disabled}
                    onValueChange={(value) => setCount(field.key, value)}
                  />
                )}
              </Field>
            ),
          )}
          <TextField
            label="Complément de référence"
            hint="E = Étage · M = Maison · C = Commerce"
            defaultValue={dwelling.referenceComplement ?? null}
            disabled={disabled}
            onValueChange={(referenceComplement) => setDwelling({ referenceComplement })}
          />
          <ResidenceTypeField
            value={residenceType}
            disabled={disabled}
            onValueChange={(value) => setDwelling({ residenceType: value })}
          />
        </div>
      </CardBody>
    </Card>
  )
}

/** Le type de résidence, que l'agent choisit (Sentry TERRAIN-9). */
function ResidenceTypeField({
  value,
  disabled,
  onValueChange,
}: {
  value: string | null
  disabled?: boolean
  onValueChange: (value: string | null) => void
}) {
  /**
   * ⚠️ Le cas courant — `value === null`, un relevé neuf — passe par la **même** branche que la
   * liste connue, et c'est ce qui garde l'identité du tableau constante d'un rendu à l'autre :
   * passée en `items` à Base UI, une liste neuve à chaque rendu défait toute mémoïsation en aval.
   *
   * Une valeur d'avant — l'ancienne liste de #118, ou une saisie libre de la v1 — reste
   * sélectionnable : la retirer de la liste l'effacerait au premier changement d'un autre champ
   * (M21).
   */
  const known = isResidenceType(value) || value === null
  const options = known
    ? [...RESIDENCE_TYPE_OPTIONS, UNSET_OPTION]
    : [
        ...RESIDENCE_TYPE_OPTIONS,
        { value, label: residenceTypeLabel(value) ?? value },
        UNSET_OPTION,
      ]

  return (
    <Field label="Type de résidence">
      {({ labelId }) => (
        <Select
          aria-labelledby={labelId}
          options={options}
          value={value}
          disabled={disabled}
          placeholder="À choisir"
          onValueChange={(choice) => onValueChange(choice === UNSET_RESIDENCE_TYPE ? null : choice)}
        />
      )}
    </Field>
  )
}
